import React, { useEffect, useRef, useState } from "react";
import { StyledSessionWrapper, StyledSessionBody } from "../session.style";
import styled from "@emotion/styled";
import { VirtuosoMessageListLicense, VirtuosoMessageList } from "@virtuoso.dev/message-list";
import type { ItemLocation, VirtuosoMessageListMethods } from "@virtuoso.dev/message-list";
import { SessionItem, SessionRenderingContext } from "../session-item/session-item.utils";
import { SessionItemRenderer } from "@/mds/components/sessions/session-item/session-item-renderer";
import { MDSSessionContextValue } from "@/mds/components/sessions/session-context";
import { useSessionRenderContext } from "./session-list.utils";
import { isEqual, last } from "lodash-es";

type Props = Pick<
  MDSSessionContextValue,
  "sessionStatus" | "activeArtifact" | "sessionConfig" | "sessionViewMode"
> & {
  sessionItems: SessionItem[];
  sessionId: string;
  containerHeight: number | string;
  /**
   * When true, disables the top fade gradient applied via `&::before`.
   * Defaults to false (top fade enabled).
   */
  disableTopFade?: boolean;
};

// Internal component that uses the context
export const MDSSessionList: React.FC<Props> = ({
  sessionConfig,
  sessionStatus,
  sessionItems,
  sessionId,
  sessionViewMode,
  containerHeight,
  disableTopFade,
}) => {
  const messagesRef =
    useRef<VirtuosoMessageListMethods<SessionItem, SessionRenderingContext>>(null);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const prevSessionItemsRef = useRef<SessionItem[]>([]);
  const prevSessionIdRef = useRef<string>(sessionId);

  // Update the virtualized message list when session items change
  // This handles both initial render and subsequent updates with different behaviors:
  // - Initial render: instant positioning with item size cache purging for accurate sizing
  // - Subsequent updates: optimized append vs full replace based on diff
  // - Smooth scrolling animation for new items
  // Note that virtuoso has dedicated "initialLocation" prop that does only part of the job unfortunately
  // The bug was:
  // - render component in deeper hierarchy (like chrome ext). it renders, all good
  // - unmount parent component and mount it back on
  useEffect(() => {
    if (messagesRef.current && sessionItems.length > 0) {
      const isInitialRender = !hasInitiallyRendered;
      const hasSessionIdChanged = prevSessionIdRef.current !== sessionId;

      const bottomOffset = messagesRef?.current?.getScrollLocation().bottomOffset || 0;
      const isNearBottom = bottomOffset < 100;

      /**
       * If we are re-rendering, but the number of items hasn't changed, we don't
       * trigger auto-scrolling.
       *
       * We have one exception, which is if the final item has been modified in
       * some way (this can happen if a part is being updated by the backend, or
       * if it flips from "loading" to "placeholder", etc.)
       */
      const shouldAutoScroll = (() => {
        if (isInitialRender) {
          return true;
        }

        if (prevSessionItemsRef.current.length === sessionItems.length) {
          const previousLastItem = last(prevSessionItemsRef.current);
          const currentLastItem = last(sessionItems);

          if (isEqual(previousLastItem, currentLastItem)) {
            return false;
          }
        }

        return isNearBottom;
      })();

      if (!sessionId) return;
      // Use smooth scroll only when session ID changes, otherwise use instant
      const scrollBehavior = hasSessionIdChanged ? "instant" : customSmoothScroll;

      const initialLocation: ItemLocation = {
        index: "LAST",
        align: "end",
        behavior: scrollBehavior,
        offset: 100,
      };

      messagesRef.current.data.replace(sessionItems, {
        initialLocation: shouldAutoScroll ? initialLocation : undefined,
      });

      if (isInitialRender) {
        setHasInitiallyRendered(true);
      }
    }

    // Update the refs for next comparison
    prevSessionItemsRef.current = sessionItems;
    prevSessionIdRef.current = sessionId;
  }, [hasInitiallyRendered, sessionItems, sessionId]);

  const context = useSessionRenderContext({
    sessionStatus,
    sessionViewMode,
    sessionItems,
    sessionConfig,
  });

  return (
    <StyledSessionWrapper data-mds-name="MDSSessionList">
      {/* Session Body */}
      <StyledSessionBody data-component="StyledSessionBody">
        {/* Session Messages */}
        <MessagesContainer disableTopFade={disableTopFade} data-component="MessagesContainer">
          <VirtuosoMessageListLicense licenseKey={import.meta.env.VITE_VIRTUOSO_KEY}>
            <VirtuosoMessageList
              ref={messagesRef}
              ItemContent={SessionItemRenderer}
              context={context}
              initialData={sessionItems}
              computeItemKey={params => params.data.id}
              style={{
                height: containerHeight,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehaviorY: "contain",
                paddingLeft: 24,
                paddingRight: 24,
                transition: "height 0.2s ease",
              }}
              shortSizeAlign="bottom"
              Header={SessionListHeader}
              Footer={SessionListFooter}
            />
          </VirtuosoMessageListLicense>
        </MessagesContainer>
      </StyledSessionBody>
    </StyledSessionWrapper>
  );
};

const MessagesContainer = styled("div", {
  // Avoid forwarding styling prop to the DOM
  shouldForwardProp: prop => prop !== "disableTopFade",
})<{
  disableTopFade?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Hide scrollbars */
  & [data-virtuoso-scroller] {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;

const customSmoothScroll = () => {
  const easeOutQuad = (x: number): number => 1 - (1 - x) * (1 - x);

  return {
    // Increase the animation frame count to smoothen and slow down the scroll.
    animationFrameCount: 10,
    easing: easeOutQuad,
  };
};

const SessionListHeader = () => {
  return <StyledSessionListHeader />;
};

const StyledSessionListHeader = styled.div`
  display: block;
  height: ${({ theme }) => theme.spacing.md};
`;

const SessionListFooter = () => {
  return <StyledSessionListFooter />;
};

const StyledSessionListFooter = styled.div`
  display: block;
  height: ${({ theme }) => theme.spacing.xxs};
`;
